import {
  TableauConnectedAppTrustType,
  DefaultApi,
  FeatureFlag,
  UpsertTableauAppInfoRequest,
} from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Ajv, { JSONSchemaType } from "ajv";
import { useForm } from "antd/lib/form/Form";
import * as React from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { AppState } from "src/store";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import {
  Alert,
  AlertExclamation,
  BorderedSelect,
  Button,
  CaretRightBlack,
  FlexRow,
  Form,
  FormItem,
  ImportTypeIcon,
  Input,
  List,
  LoaderWithPerfTimings,
  message,
  Modal,
  OAuthConfiguredAlert,
  SettingsLayout,
} from "../components";
import { COLORS } from "../constants";
import { useApiContext, useTrack } from "../utils";

const HelperText = styled.div`
  color: ${COLORS.black45};
  margin-bottom: 20px;
`;

const StyledAlert = styled(Alert)<{ $background?: string; $marginBottom?: string }>`
  line-height: 1.8em;
  padding: 12px;

  code {
    white-space: nowrap;
    background-color: ${COLORS.black4};
    padding: 4px 6px;
  }

  .ant-alert-icon {
    margin: 6px;
  }

  ${({ $background }) => ($background ? `background-color: ${$background};` : "")}
  ${({ $marginBottom }) => ($marginBottom ? `margin-bottom: ${$marginBottom};` : "")}
`;

const StyledModal = styled(Modal)`
  &.ant-modal {
    .ant-modal-body {
      padding: 5px;
    }
  }
`;

const StyledLabel = styled.div`
  label {
    font-weight: bold;
  }

  margin: 6px 0;
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  margin: 8px 16px;
`;

const StyledSummaryText = styled.div`
  margin-right: 12px;
  color: ${COLORS.black45};
`;

const { Item } = List;

const DataSourceSelections = styled.div`
  border-radius: 6px;
  cursor: default !important;

  .ant-list-item {
    .actionswrap {
      visibility: visible !important;
      border-radius: 6px !important;
    }
  }
`;

const StyledItem = styled(Item)`
  && {
    background: ${COLORS.black2};
    border: none;
    cursor: pointer;
    position: relative;
    margin-bottom: 8px;

    &:hover,
    &:focus-within,
    &:focus,
    &:active {
      background-color: ${COLORS.black4};

      .actionswrap {
        visibility: visible;
      }
    }

    .actionswrap {
      visibility: hidden;
    }

    .ant-list-item {
      margin: 12px 15px !important;
      border: 10px;
      background-color: ${COLORS.black4} !important;
      border-radius: 6px !important;
      cursor: default !important;

      .actionswrap {
        visibility: visible !important;
        border-radius: 6px !important;
      }
    }

    .ant-list-item-meta {
      align-items: center;
    }

    .ant-list-item-meta-title {
      color: ${COLORS.black45};
      line-height: 1.25;

      h4 {
        margin: 0px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
      }
    }

    .ant-list-item-meta-description {
      color: ${COLORS.black85};
      font-size: 14px;
      font-weight: bold;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
  }
`;

const ajv = new Ajv();
const schema: JSONSchemaType<UpsertTableauAppInfoRequest> = {
  type: "object",
  properties: {
    issuer_uri: { type: "string", nullable: true },
    client_id: { type: "string" },
    client_secret: { type: "string" },
    domain: { type: "string", nullable: true },
    tableau_connected_app_trust_type: { type: "string", nullable: true },
    client_secret_id: { type: "string", nullable: true },
    instance_url: { type: "string", nullable: true },
    site_name: { type: "string", nullable: true },
    app_name: { type: "string", nullable: true },
  },
  required: ["client_id", "client_secret"],
};

const validate = ajv.compile(schema);

const fetchTableauAppInfo = async (api: DefaultApi) => {
  const response = await api.listTableauAppInfo();
  return response?.data?.apps_list;
};

export const TableauOAuthSettings = () => {
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const [showSuccessModal, setShowSuccessModal] = React.useState(false);

  const [form] = useForm<UpsertTableauAppInfoRequest>();

  const { value: enabletableauOauthDirectTrustOption } = useFeatureGate(
    FeatureFlag.EnableTableauOauthDirectTrustOption
  );

  const { value: enableTableauOauthViaOkta } = useFeatureGate(
    FeatureFlag.EnableTableauOauthViaOkta
  );

  const isTableauOauthEnabled = enableTableauOauthViaOkta || enabletableauOauthDirectTrustOption;

  const tableauOauthOptions = [
    enableTableauOauthViaOkta && {
      label: "OAuth 2.0 Trust",
      value: TableauConnectedAppTrustType.OauthTrust,
    },
    enabletableauOauthDirectTrustOption && {
      label: "Direct Trust",
      value: TableauConnectedAppTrustType.DirectTrust,
    },
  ].filter(Boolean) as Array<{ label: string; value: TableauConnectedAppTrustType }>;

  const [selectedTrustType, setSelectedTrustType] = React.useState<TableauConnectedAppTrustType>(
    tableauOauthOptions[0]?.value || TableauConnectedAppTrustType.OauthTrust
  );

  const track = useTrack();

  const [errorMessage, setErrorMessage] = React.useState("");

  const location = useLocation();

  const history = useHistory();

  const { url } = useRouteMatch();

  const searchParams = new URLSearchParams(location.search);

  const redirectOnAdd = searchParams.get("back_on_add");

  const isAddNewAccount = searchParams.get("addNewAccount") === "true";

  const [showAddNewAccountForm, setShowAddNewAccountForm] = React.useState(isAddNewAccount);

  const isEnabled = !clientFlags?.is_gmail_like_domain;

  // Reset form fields and trust type state when showing add form
  React.useEffect(() => {
    if (showAddNewAccountForm) {
      const defaultTrustType =
        tableauOauthOptions[0]?.value || TableauConnectedAppTrustType.OauthTrust;
      setSelectedTrustType(defaultTrustType);
      form.setFieldsValue({
        tableau_connected_app_trust_type: defaultTrustType,
        issuer_uri: "",
        client_id: "",
        client_secret: "",
        client_secret_id: "",
        instance_url: "",
        site_name: "",
        app_name: "",
      });
    }
  }, [showAddNewAccountForm]);

  React.useEffect(() => {
    document.title = "Tableau OAuth Settings - Coefficient Workspace";
    track("workspace_tableau_oauth_settings_viewed");
  }, []);

  const { apiClient: api } = useApiContext();

  const queryClient = useQueryClient();

  const {
    data: tableauAppInfoList,
    isLoading,
    isError,
  } = useQuery([QueryCacheKey.TABLEAU_APP_INFO], () => fetchTableauAppInfo(api), {
    cacheTime: 0,
    staleTime: 0,
    enabled: isEnabled,
  });

  const checkConnectionMutation = useMutation(
    async (data: UpsertTableauAppInfoRequest) => {
      if (data.tableau_connected_app_trust_type !== TableauConnectedAppTrustType.DirectTrust) {
        const wellKnownUrl = `${data.issuer_uri}/.well-known/openid-configuration`;
        const response = await fetch(wellKnownUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Invalid Issuer URL: OpenID configuration unavailable on issuer URL");
        }
        return data;
      } else {
        // Verify Direct Trust credentials before saving
        const checkResponse = await api.tableauCheckConnection({
          tableau_check_connection_request: {
            credentials: {
              url: data.instance_url,
              client_id: data.client_id,
              client_secret: data.client_secret,
              client_secret_id: data.client_secret_id,
              tableau_site_name: data.site_name,
              tableau_connected_app_trust_type: TableauConnectedAppTrustType.DirectTrust,
              username: clientFlags?.email,
            },
          },
        });

        if (!checkResponse.data.valid) {
          const errorMsg = checkResponse.data.error || "Connection verification failed";
          throw new Error(checkResponse.data.title || errorMsg);
        }
        return data;
      }
    },
    {
      onSuccess: data => {
        upsertAppInfoMutation.mutate(data);
      },
      onError: (error: any) => {
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again.";
        setErrorMessage(errorMsg);
        message.error(errorMsg);
      },
    }
  );

  const upsertAppInfoMutation = useMutation(
    (json: UpsertTableauAppInfoRequest) =>
      api.upsertTableauAppInfo({
        upsert_tableau_app_info_request: {
          issuer_uri: json.issuer_uri,
          client_id: json.client_id,
          client_secret: json.client_secret,
          tableau_connected_app_trust_type: json.tableau_connected_app_trust_type,
          client_secret_id: json.client_secret_id,
          instance_url: json.instance_url,
          site_name: json.site_name,
          app_name: json.app_name,
        },
      }),
    {
      onSuccess: () => {
        message.success("Saved successfully");
        setShowSuccessModal(true);
        setShowAddNewAccountForm(false);
        form.resetFields();
        queryClient.invalidateQueries([QueryCacheKey.TABLEAU_APP_INFO]);
        if (redirectOnAdd) {
          history.goBack();
        }
      },
      onError: (error: any) => {
        const errorMessage =
          error.response?.data?.message || "Something went wrong. Please try again.";
        setErrorMessage(errorMessage);
        message.error("Failed to save configuration");
      },
    }
  );

  if (!isEnabled) {
    return (
      <SettingsLayout title="Tableau OAuth Settings" dataSourceType="tableau" width="narrow">
        <Alert
          type="error"
          showIcon
          message="Tableau OAuth is not supported for your domain"
          description={`Tableau OAuth is not supported for ${
            clientFlags?.email.split("@")[1]
          }. Please use a different account to configure Tableau OAuth.`}
        />
      </SettingsLayout>
    );
  }

  if (
    clientFlags?.admin_emails &&
    clientFlags?.admin_emails.length > 0 &&
    !clientFlags?.admin_emails?.includes(clientFlags.email)
  ) {
    return (
      <SettingsLayout title="Tableau OAuth Settings" dataSourceType="tableau" width="narrow">
        <StyledAlert
          type="error"
          showIcon
          icon={<AlertExclamation type="error" />}
          message="You need to be an admin to access this page"
          description="Please contact your domain admin for configuring or managing Tableau accounts."
        />
      </SettingsLayout>
    );
  }

  if (isLoading) {
    return (
      <SettingsLayout title="Tableau OAuth Settings" dataSourceType="tableau" width="narrow">
        <LoaderWithPerfTimings name="TableauOAuthSettings" />
      </SettingsLayout>
    );
  }

  if (isError) {
    return (
      <SettingsLayout title="Tableau OAuth Settings" dataSourceType="tableau" width="narrow">
        <StyledAlert
          type="error"
          showIcon
          icon={<AlertExclamation type="error" />}
          message="Error loading Tableau OAuth Settings"
          description="Please try refreshing this page, or contact support@coefficient.io"
        />
      </SettingsLayout>
    );
  }

  const handleOnFinish = (data: UpsertTableauAppInfoRequest) => {
    const valid = validate(data);
    if (valid) {
      checkConnectionMutation.mutate({
        ...form.getFieldsValue([
          "tableau_connected_app_trust_type",
          "issuer_uri",
          "client_id",
          "client_secret",
          "client_secret_id",
          "instance_url",
          "site_name",
          "app_name",
        ]),
      });
    }
  };

  // Show list view when apps exist and not in add mode
  if (tableauAppInfoList && tableauAppInfoList.length > 0 && !showAddNewAccountForm) {
    return (
      <SettingsLayout title="Tableau OAuth Settings" dataSourceType="tableau" width="narrow">
        <OAuthConfiguredAlert dataSourceType="tableau" $marginBottom="20px" />
        <Form>
          <StyledLabel>
            <label>Accounts</label>
          </StyledLabel>
        </Form>
        {tableauAppInfoList.map((appInfo, index) => (
          <DataSourceSelections key={index}>
            <StyledItem onClick={() => null}>
              <Item.Meta
                title="App Name"
                avatar={<ImportTypeIcon type={"tableau"} />}
                description={appInfo.app_name}
              />
              <StyledLink to={`${url}/${encodeURIComponent(appInfo.client_id ?? "")}`}>
                <StyledSummaryText>Manage</StyledSummaryText>
                <CaretRightBlack />
              </StyledLink>
            </StyledItem>
          </DataSourceSelections>
        ))}

        <Button
          style={{ width: 200, marginTop: 16 }}
          onClick={() => {
            setShowAddNewAccountForm(true);
            setErrorMessage("");
          }}
        >
          Add Connection
        </Button>
        <StyledModal open={showSuccessModal} onCancel={() => setShowSuccessModal(false)}>
          <StyledAlert
            showIcon
            banner
            type="success"
            message="Saved successfully"
            description={`Connect to Tableau OAuth in the Coefficient Sidebar in your spreadsheet.`}
            $background="white"
          />
        </StyledModal>
      </SettingsLayout>
    );
  }

  // Show add form (for new account or when no accounts exist)
  return (
    <SettingsLayout title="Tableau OAuth Settings" dataSourceType="tableau" width="narrow">
      {errorMessage && (
        <StyledAlert
          showIcon
          type="error"
          icon={<AlertExclamation type="error" />}
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <Form
        layout="vertical"
        onFinish={handleOnFinish}
        onBeforeInput={() => setErrorMessage("")}
        initialValues={{
          issuer_uri: "",
          client_id: "",
          client_secret: "",
          tableau_connected_app_trust_type:
            tableauOauthOptions[0]?.value || TableauConnectedAppTrustType.OauthTrust,
          client_secret_id: "",
          instance_url: "",
          site_name: "",
          app_name: "",
        }}
        disabled={isLoading || checkConnectionMutation.isLoading || upsertAppInfoMutation.isLoading}
        form={form}
      >
        <HelperText>
          Allows users from your domain to connect to Tableau using OAuth. You must be a Tableau
          admin to configure.{" "}
          <a
            href="https://coefficient.notion.site/Tableau-OAuth-via-Okta-1ee0952b59ee803e810dcd9cd6093f9b?pvs=4"
            target="_blank"
            onClick={() => {
              track("help_center_clicked", {
                event_from: "workspace_tableau_oauth_settings",
                import_data_source: "tableau",
              });
            }}
          >
            Help guide
          </a>
        </HelperText>

        {isTableauOauthEnabled && (
          <FormItem
            label="Connected App Trust Type"
            name="tableau_connected_app_trust_type"
            rules={[{ required: true, message: "Trust type is required" }]}
          >
            <BorderedSelect
              placeholder="Select trust type"
              style={{ width: "33%" }}
              options={tableauOauthOptions}
              onChange={(value: TableauConnectedAppTrustType) => setSelectedTrustType(value)}
            />
          </FormItem>
        )}

        <FormItem
          label="Name"
          name="app_name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="Name" />
        </FormItem>

        {selectedTrustType !== TableauConnectedAppTrustType.DirectTrust && (
          <FormItem
            label="Issuer URI"
            name="issuer_uri"
            rules={[{ required: true, message: "Issuer URI is required" }]}
          >
            <Input placeholder="https://example.cloud.tableau.com" />
          </FormItem>
        )}

        <FormItem
          label="Client ID"
          name="client_id"
          rules={[{ required: true, message: "Client ID is required" }]}
        >
          <Input placeholder="Client ID" />
        </FormItem>
        {selectedTrustType === TableauConnectedAppTrustType.DirectTrust && (
          <FormItem
            label="Client Secret ID"
            name="client_secret_id"
            rules={[{ required: true, message: "Client Secret ID is required" }]}
          >
            <Input placeholder="Client Secret ID" />
          </FormItem>
        )}
        <FormItem
          label="Client Secret"
          name="client_secret"
          rules={[{ required: true, message: "Client Secret is required" }]}
        >
          <Input type="password" placeholder="Client Secret" />
        </FormItem>

        <FormItem
          label="Instance URL"
          name="instance_url"
          rules={[{ required: true, message: "Tableau Instance URL is required" }]}
        >
          <Input placeholder="Your Tableau Instance URL" />
        </FormItem>

        <FormItem label="Site Name" name="site_name" tooltip="Leave blank for the Default site">
          <Input placeholder="Leave blank for the Default site" />
        </FormItem>

        <FlexRow gap={1}>
          {(showAddNewAccountForm || redirectOnAdd) && (
            <Button
              type="text"
              onClick={() => {
                if (redirectOnAdd) {
                  history.goBack();
                } else {
                  setShowAddNewAccountForm(false);
                  form.resetFields();
                  setErrorMessage("");
                }
              }}
              style={{ marginLeft: 10 }}
              disabled={
                isLoading || checkConnectionMutation.isLoading || upsertAppInfoMutation.isLoading
              }
            >
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            loading={
              isLoading || checkConnectionMutation.isLoading || upsertAppInfoMutation.isLoading
            }
          >
            Save
          </Button>
        </FlexRow>
      </Form>
      <StyledModal open={showSuccessModal} onCancel={() => setShowSuccessModal(false)}>
        <StyledAlert
          showIcon
          banner
          type="success"
          message="Saved successfully"
          description={`Connect to Tableau OAuth in the Coefficient Sidebar in your spreadsheet.`}
          $background="white"
        />
      </StyledModal>
    </SettingsLayout>
  );
};
